import { useEffect, useLayoutEffect, useRef } from 'react';
import { useSearchParams } from 'react-router';

interface UseUqlUrlSyncOptions<TQuery> {
  enabled: boolean;
  parse: (uql: string | null) => TQuery;
  build: (query: TQuery) => string;
  /** Writes a parsed query into the page store. */
  apply: (query: TQuery) => void;
  /** The page store's current query, rebuilt each render. */
  current: TQuery;
}

/**
 * Mirrors a list page's filters, sort and page into a `uql` search param so a
 * view deep-links and survives refresh and back/forward. URL → store whenever
 * the param changes; store → URL (replace) whenever the query changes.
 */
export function useUqlUrlSync<TQuery>({
  enabled,
  parse,
  build,
  apply,
  current,
}: UseUqlUrlSyncOptions<TQuery>) {
  const [searchParams, setSearchParams] = useSearchParams();
  const uqlParam = searchParams.get('uql');
  const searchParamsKey = searchParams.toString();
  const skipNextUrlSyncRef = useRef(false);
  const parseRef = useRef(parse);
  const applyRef = useRef(apply);
  parseRef.current = parse;
  applyRef.current = apply;

  useLayoutEffect(() => {
    skipNextUrlSyncRef.current = true;
    applyRef.current(parseRef.current(uqlParam));
  }, [uqlParam]);

  const nextUql = build(current);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (skipNextUrlSyncRef.current) {
      skipNextUrlSyncRef.current = false;
      return;
    }

    const params = new URLSearchParams(searchParamsKey);
    if ((params.get('uql') ?? '') === nextUql) {
      return;
    }

    if (nextUql) {
      params.set('uql', nextUql);
    } else {
      params.delete('uql');
    }

    setSearchParams(params, { replace: true });
  }, [enabled, nextUql, searchParamsKey, setSearchParams]);
}
